
export interface Template {
  id: string; name: string; category: string;
  template: string; fields: string[]; example: Record<string, string>;
}

export const TEMPLATE_LIBRARY: Template[] = [
  {
    id: 'content', name: 'Content Creation', category: 'Marketing',
    template: 'Act as a [ROLE]. Create [FORMAT] about [TOPIC] for [AUDIENCE]. Tone: [TONE]. Include: [ELEMENTS]. Length: [LENGTH].',
    fields: ['ROLE','FORMAT','TOPIC','AUDIENCE','TONE','ELEMENTS','LENGTH'],
    example: { ROLE: 'senior copywriter', FORMAT: 'blog post', TOPIC: 'AI productivity', AUDIENCE: 'freelancers', TONE: 'professional', ELEMENTS: 'headings, bullets, CTA', LENGTH: '800 words' }
  },
  {
    id: 'code', name: 'Code Generation', category: 'Development',
    template: 'You are a [LANGUAGE] expert. Write [TYPE] that [FUNCTION]. Requirements: [REQS]. Include: [INCLUDES]. Error handling: [ERRORS].',
    fields: ['LANGUAGE','TYPE','FUNCTION','REQS','INCLUDES','ERRORS'],
    example: { LANGUAGE: 'TypeScript', TYPE: 'React component', FUNCTION: 'renders a searchable data table', REQS: 'sortable columns, pagination, 100 rows max', INCLUDES: 'loading state, empty state', ERRORS: 'network fallback, timeout handling' }
  },
  {
    id: 'ba', name: 'Business Analysis', category: 'BA',
    template: 'As a [SENIORITY] Business Analyst, analyze [ARTIFACT] for [SYSTEM]. Deliver: [DELIVERABLES]. Standards: [STANDARDS]. Stakeholders: [STAKEHOLDERS].',
    fields: ['SENIORITY','ARTIFACT','SYSTEM','DELIVERABLES','STANDARDS','STAKEHOLDERS'],
    example: { SENIORITY: 'Senior', ARTIFACT: 'user onboarding flow', SYSTEM: 'SaaS platform', DELIVERABLES: 'user stories, process map, gap analysis', STANDARDS: 'IEEE 830, BPMN 2.0', STAKEHOLDERS: 'Product Owner, UX Lead, Engineering Manager' }
  },
  {
    id: 'data', name: 'Data Analysis', category: 'Analytics',
    template: 'You are a [ROLE] data analyst. Analyze [DATASET] to answer: [QUESTION]. Methods: [METHODS]. Output: [OUTPUT_FORMAT]. Visualizations: [VIZ].',
    fields: ['ROLE','DATASET','QUESTION','METHODS','OUTPUT_FORMAT','VIZ'],
    example: { ROLE: 'Senior', DATASET: 'Q3 sales transactions', QUESTION: 'Which product categories drive 80% of revenue?', METHODS: 'Pareto analysis, cohort comparison', OUTPUT_FORMAT: 'executive summary with charts', VIZ: 'bar chart, trend line' }
  },
  {
    id: 'email', name: 'Professional Email', category: 'Communication',
    template: 'Write a [TONE] email to [RECIPIENT] about [TOPIC]. Goal: [GOAL]. Context: [CONTEXT]. Required elements: [ELEMENTS].',
    fields: ['TONE','RECIPIENT','TOPIC','GOAL','CONTEXT','ELEMENTS'],
    example: { TONE: 'professional but firm', RECIPIENT: 'client', TOPIC: 'scope change request', GOAL: 'get written approval for extra budget', CONTEXT: 'mid-project, new requirements added', ELEMENTS: 'impact summary, cost breakdown, deadline effect' }
  }
];

export interface RefinementResult {
  score: number; issues: string[]; category: string | null;
  refined: string; template: Template | null;
}

const REFINEMENT_RULES = [
  { pattern: /^(write|create|make|generate)/i, fix: 'Add role assignment: "Act as a [expert role] and..."' },
  { pattern: /\?$/g, fix: 'Convert question to instruction: "Analyze X and provide Y"' },
  { pattern: /(good|better|best|nice|great)/gi, fix: 'Replace vague adjectives with measurable criteria' },
  { pattern: /(etc\.?|and so on|and more|and stuff)/gi, fix: 'List specific examples instead of vague endings' },
  { pattern: /(fast|quick|easy|simple|user-friendly)/gi, fix: 'Define specific performance or usability requirements' },
  { pattern: /(something|anything|whatever|some kind of)/gi, fix: 'Be specific about deliverables and format' },
];

export function detectCategory(text: string): string | null {
  const t = text.toLowerCase();
  if (/(code|function|component|api|bug|debug|react|python|js|ts)/.test(t)) return 'code';
  if (/(blog|article|email|copy|content|social|linkedin|twitter|post)/.test(t)) return 'content';
  if (/(requirement|user story|acceptance criteria|use case|process|workflow|bpmn)/.test(t)) return 'ba';
  if (/(data|chart|table|csv|excel|sql|analytics|metric|kpi|revenue|sales)/.test(t)) return 'data';
  if (/(email|letter|message|to:|dear|hi\s+\w+)/.test(t)) return 'email';
  return null;
}

export function refinePromptLogic(raw: string): RefinementResult {
  let issues: string[] = [];
  let score = 100;
  let category = detectCategory(raw);

  REFINEMENT_RULES.forEach(rule => {
    if (rule.pattern.test(raw)) {
      issues.push(rule.fix);
      score -= 10;
    }
  });

  if (!raw.includes('"') && !raw.includes("'")) {
    issues.push('Add example output in quotes to guide format');
    score -= 15;
  }
  if (raw.length < 50) {
    issues.push('Expand context — add audience, goal, and constraints');
    score -= 20;
  }
  if (!/[1-9]\./.test(raw) && !/bullet|list|steps|numbered/i.test(raw)) {
    issues.push('Request structured output (numbered list or bullets)');
    score -= 10;
  }
  if (!/(as a|act as|you are|role:)/i.test(raw)) {
    issues.push('Assign a specific role/persona to the AI');
    score -= 10;
  }

  const matchedTemplate = category ? TEMPLATE_LIBRARY.find(t => t.id === category) : null;
  let refined = raw;
  if (matchedTemplate) {
    refined = `【Template Match: ${matchedTemplate.name}】\n\n${matchedTemplate.template}\n\nFill in the blanks with your specifics, or use the example below as guidance.`;
  } else {
    refined = `【Logic-Refined Prompt】\n\nAct as an expert in [DOMAIN]. ${raw.trim()}\n\nDeliver the output as: [SPECIFY FORMAT].\nInclude: [LIST ELEMENTS].\nConstraints: [ADD LIMITATIONS].\nExample of desired output: [PASTE EXAMPLE].`;
  }

  return { score: Math.max(0, score), issues, category, refined, template: matchedTemplate || null };
}

export function fillTemplate(template: Template, values: Record<string, string>): string {
  let result = template.template;
  template.fields.forEach(f => {
    result = result.replace(`[${f}]`, values[f] || `[${f}]`);
  });
  return result;
}
